import * as React from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { LanguageContext } from "~/providers/LanguageProvider";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { useReadLocalStorage } from "usehooks-ts";
import { z } from "zod";
import { fetcher } from "~/hooks/useFetcher";
import ChevronRight from "~/ui/Icon/ChevronRight";

const CategorySchema = z.object({
    DocumentTypeID: z.number(),
    Name: z.string(),
    FrName: z.string(),
    Count: z.number(),
});

type CategorySchemaType = z.infer<typeof CategorySchema>;

const Categories = () => {
    const { t } = useTranslation();
    const token = useReadLocalStorage("_t") as string;
    const { language } = React.useContext(LanguageContext);

    const titleStyle = clsx("h-8 gap-6 font-medium-light text-2xl leading-8 tracking-[-2%] text-stone-darkest");

    const { data } = useSWR("/api/market-intelligence/category-count", (url) => fetcher(url, token));

    const renderSkeleton = () => (
    <>
        {[...Array(4)].map((_, index) => (
        <div className="flex items-center justify-center" key={index}>
            <div className="bg-white text-left w-[768px] h-16 rounded-2xl border border-gray-300 flex items-center px-4 py-3 gap-3 drop-shadow-sm">
            {/* Skeleton for label */}
            <span className="flex-grow h-6 bg-gray-200 rounded-full animate-pulse" />
            {/* Skeleton for count */}
            <span className="w-8 h-6 bg-gray-200 rounded-full animate-pulse" />
            {/* Chevron skeleton */}
            <span className="w-6 h-6 bg-gray-200 rounded animate-pulse" />
            </div>
        </div>
        ))}
    </>
    );

    return (
        <div className="min-h-screen flex flex-col items-center justify-start pt-[160px] py-[80px]">
            <h1 className={titleStyle}>{t("marketIntelligenceHeader")}</h1>
            <div className="bg-white rounded-2xl py-12 w-[61.375rem] mx-auto grid grid-cols-1 gap-4">
                {!data && renderSkeleton()}
                {data &&
                    data.map((category: CategorySchemaType) => (
                        <CategoryButton
                            key={category.DocumentTypeID}
                            link={category.DocumentTypeID.toString()}
                            label={language === "en" ? category.Name : category.FrName}
                            count={category.Count}
                        />
                    ))}
            </div>
        </div>
    );
};

interface CategoryButtonProps {
    link: string;
    label: string;
    count?: number | null;
    openTab?: boolean;
}

const CategoryButton = (props: CategoryButtonProps) => {
    const { link, count, label, openTab = false } = props;
    return (
        <div className="flex items-center justify-center">
            <Link
                to={`/market-intelligence/${link}`}
                className="bg-white text-left text-md gap-3 pt-3 pr-5 pb-3 pl-4 text-foreground-dark font-medium-light w-[768px] h-16 rounded-2xl border border-white flex items-center drop-shadow-md"
                target={openTab ? "_blank" : "_self"}
            >
                <span className="flex-grow text-base text-stone-vibrant rounded-full py-2 gap-1.5 leading-6 tracking-[-2%] opacity-100">{label}</span>
                {count && <span className="text-base mr-2">{count}</span>}
                <ChevronRight />
            </Link>
        </div>
    );
};

export default Categories;
